import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import SeoMeta, { SITE_URL, makeDescription } from "../components/common/SeoMeta";
import { fetchPublicContent } from "../data/publicContent";
import "./styles/blog-public.css";

function formatDate(iso) {
	if (!iso) return "";
	return new Date(`${iso}T00:00:00`).toLocaleDateString("en-ZA", {
		day: "numeric", month: "long", year: "numeric",
	});
}

function estimateReadMinutes(body = "") {
	const words = body.trim().split(/\s+/).filter(Boolean).length;
	return Math.max(1, Math.round(words / 200));
}

const SkeletonGrid = ({ count = 3 }) => (
	<div className="pb-skeleton-grid">
		{Array.from({ length: count }).map((_, i) => (
			<div className="pb-skeleton-card" key={i}>
				<div className="pb-skeleton-cover" />
				<div className="pb-skeleton-lines">
					<div className="pb-skeleton-line pb-skeleton-line--short" />
					<div className="pb-skeleton-line pb-skeleton-line--wide" />
					<div className="pb-skeleton-line pb-skeleton-line--mid" />
				</div>
			</div>
		))}
	</div>
);

const PostCard = ({ post, index }) => (
	<Link
		to={`/blog/${post.id}`}
		className="pb-card"
		style={{ animationDelay: `${140 + index * 50}ms` }}
	>
		<div className="pb-card__cover">
			{post.coverUrl ? (
				<img className="pb-card__cover-img" src={post.coverUrl} alt={post.title} loading="lazy" />
			) : (
				<div className="pb-card__cover-fallback">Jz</div>
			)}
		</div>
		<div className="pb-card__body">
			{post.keywords?.[0] && <span className="pb-card__tag">{post.keywords[0]}</span>}
			<h2 className="pb-card__title">{post.title}</h2>
			{post.description && <p className="pb-card__desc">{post.description}</p>}
			<div className="pb-card__meta">
				<span>{formatDate(post.date)}</span>
				<span className="pb-dot">·</span>
				<span>{estimateReadMinutes(post.body)} min read</span>
			</div>
		</div>
	</Link>
);

const HeroCard = ({ post }) => (
	<Link to={`/blog/${post.id}`} className="pb-hero">
		<div className="pb-hero__media">
			{post.coverUrl ? (
				<img className="pb-hero__media-img" src={post.coverUrl} alt={post.title} />
			) : (
				<div className="pb-hero__fallback">Jz</div>
			)}
		</div>
		<div className="pb-hero__body">
			<p className="pb-hero__kicker">Latest post</p>
			<h2 className="pb-hero__title">{post.title}</h2>
			{post.description && <p className="pb-hero__desc">{post.description}</p>}
			<div className="pb-hero__meta">
				<span>{formatDate(post.date)}</span>
				<span className="pb-dot">·</span>
				<span>{estimateReadMinutes(post.body)} min read</span>
			</div>
		</div>
	</Link>
);

const BlogList = () => {
	const [posts, setPosts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		let cancelled = false;
		fetchPublicContent("blogs")
			.then((docs) => {
				if (cancelled) return;
				docs.sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
				setPosts(docs);
			})
			.catch((err) => {
				if (!cancelled) setError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => { cancelled = true; };
	}, []);

	const [featured, ...rest] = posts;
	const description = makeDescription(
		featured?.description,
		"Read Jabu Zwane's perspectives on leadership, personal growth, communication and mindset development."
	);
	const blogListSchema = {
		"@context": "https://schema.org",
		"@type": "Blog",
		name: "Jabu Zwane Blog",
		description,
		url: `${SITE_URL}/blog`,
		author: { "@type": "Person", name: "Jabu Zwane", url: SITE_URL },
		blogPost: posts.slice(0, 20).map((post) => ({
			"@type": "BlogPosting",
			headline: post.title,
			url: `${SITE_URL}/blog/${encodeURIComponent(post.id)}`,
			...(post.description ? { description: makeDescription(post.description) } : {}),
			...(post.coverUrl ? { image: post.coverUrl } : {}),
			...(post.date ? { datePublished: post.date } : {}),
		})),
	};

	return (
		<>
		<SeoMeta
			title="Blog | Jabu Zwane"
			description={description}
			path="/blog"
			image={featured?.coverUrl}
			structuredData={blogListSchema}
		/>
		<div className="page-content">
			<NavBar active="blog" />
			<div className="content-wrapper">
				<div className="pb-root">
					<div className="pb-header">
						<h1 className="pb-title">Blog</h1>
						<p className="pb-sub">Perspectives on leadership, mindset, communication and personal growth.</p>
					</div>

					{loading ? (
						<SkeletonGrid />
					) : error ? (
						<div className="pb-state pb-state--error">Couldn't load posts right now. Try refreshing the page.</div>
					) : posts.length === 0 ? (
						<div className="pb-state">No posts yet — check back soon.</div>
					) : (
						<>
							<HeroCard post={featured} />
							{rest.length > 0 && (
								<div className="pb-grid">
									{rest.map((post, i) => (
										<PostCard post={post} index={i} key={post.id} />
									))}
								</div>
							)}
						</>
					)}
				</div>
				<div className="page-footer"><Footer /></div>
			</div>
		</div>
		</>
	);
};

export default BlogList;